<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import del_ava from '../../../../assets/images/new_theme/icons/ava/delete-ava.png';
import { useSettingsStore } from '@/dashboard/store/settingsStore';
import { POPUPS_ANIMATION } from '@/dashboard/constants/popups';

const settingsStore = useSettingsStore();
const { isShowDeletePopup, isDeleteAccountLoader, deleteAccountConfirmation, deleteAccountError } = storeToRefs(settingsStore);
</script>

<template>
  <TransitionRoot :show="isShowDeletePopup" as="template">
    <Dialog class="fixed bottom-zero left-zero right-zero top-zero z-[5000]">
      <!-- The backdrop, rendered as a fixed sibling to the panel container -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.background.enter"
        :enter-from="POPUPS_ANIMATION.background.enterFrom"
        :enter-to="POPUPS_ANIMATION.background.enterTo"
        :leave="POPUPS_ANIMATION.background.leave"
        :leave-from="POPUPS_ANIMATION.background.leaveFrom"
        :leave-to="POPUPS_ANIMATION.background.leaveTo"
      >
        <div class="inset-0 fixed bottom-zero left-zero right-zero top-zero h-full w-full bg-gray-250/60 dark:bg-black/60" aria-hidden="true" />
      </TransitionChild>

      <!-- Full-screen container to center the panel -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.content.enter"
        :enter-from="POPUPS_ANIMATION.content.enterFrom"
        :enter-to="POPUPS_ANIMATION.content.enterTo"
        :leave="POPUPS_ANIMATION.content.leave"
        :leave-from="POPUPS_ANIMATION.content.leaveFrom"
        :leave-to="POPUPS_ANIMATION.content.leaveTo"
      >
        <div class="absolute h-full w-full overflow-auto p-[47px]">
          <div class="flex min-h-full flex-1 items-center justify-center">
            <!-- The actual dialog panel -->
            <DialogPanel
              v-if="!isDeleteAccountLoader"
              class="w-[520px] rounded-16 bg-white px-s pb-s pt-[40px] text-center dark:bg-gray-900 max-tablet:px-s"
            >
              <div class="mb-[23px] flex items-center justify-center">
                <img :src="del_ava" class="mr-[12px] h-[32px] w-[32px]" alt="" />
                <h5 class="text-black dark:text-gray-400">Account Deleting!</h5>
              </div>

              <p class="text-content-m mb-l px-l text-gray-900 dark:text-gray-500">
                Are you sure you want to permanently delete your teampaper account? If so, please enter the word "Delete" in the field below
              </p>

              <div class="mb-s">
                <div class="tppr-text-field flex-1" :class="{ error: deleteAccountError }">
                  <div class="tppr-text-field__field-container">
                    <input v-model="deleteAccountConfirmation" type="text" placeholder="Write here “Delete”" />
                  </div>
                </div>
              </div>

              <div class="flex justify-between gap-xs">
                <button v-focus class="button button-ghost h-[40px] max-w-[138px]" @click="() => settingsStore.toggleDeleteAccount()">
                  <span class="button__text">Cancel</span>
                </button>

                <button class="button button-danger h-[40px] max-w-[138px]" @click="() => settingsStore.deleteAccount()">
                  <span v-if="!isDeleteAccountLoader" class="button__text">Delete</span>
                  <svg
                    v-if="isDeleteAccountLoader"
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 22 22"
                    fill="none"
                    class="rotate"
                  >
                    <path
                      class="stroke-white dark:stroke-gray-400"
                      d="M20.5832 11C20.5832 16.2927 16.2926 20.5834 10.9998 20.5834C5.70711 20.5834 1.4165 16.2927 1.4165 11C1.4165 5.70729 5.70711 1.41669 10.9998 1.41669"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </DialogPanel>

            <DialogPanel
              v-if="isDeleteAccountLoader"
              class="flex min-h-[375px] w-[520px] items-center justify-center rounded-16 bg-white dark:bg-gray-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 22 22" fill="none" class="rotate">
                <path
                  class="stroke-black dark:stroke-gray-400"
                  d="M20.5832 11C20.5832 16.2927 16.2926 20.5834 10.9998 20.5834C5.70711 20.5834 1.4165 16.2927 1.4165 11C1.4165 5.70729 5.70711 1.41669 10.9998 1.41669"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </DialogPanel>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>
